/**
 * ProseMirror JSON -> CanonicalDocument (Part 1.2-1.3 of the WYSIWYG editor plan). The reverse of
 * fromCdm.ts; see that file's header comment for the overall architecture.
 */
import type { Comment as CdmComment } from './types'
import type { JSONContent } from '@tiptap/core'
import type {
  CanonicalDocument, DocSection, DocBlock, DocInline, TableRow, TableCell,
  ParagraphFormat, CellFormat, TableBorders, PageSetup, HeaderFooter,
  RunFormat, FieldType, FormatterSpec, SourceLocation, ListNumberFormat, RowHeightRule,
  BorderSide, TextBoxWrap as TextBoxWrapType, VAlign as VAlignType,
  CommentAnchor as CommentAnchorType,
  ContentControl, ContentControlAnchor as ContentControlAnchorType, NoteSetup, Revision,
  EmbeddedObjectKind, EmbeddedPart,
} from './types'
import type { TableAlignment, TableWidth, TableLayout, PropertiesChange, FloatingAnchor, ConditionalRun, LoopRun, TableColumnLoop } from './types'
import {
  TableAlignment as TableAlignmentEnum, TableLayout as TableLayoutEnum,
  defaultRunFormat, ListNumberFormat as ListNumberFormatEnum, RowHeightRule as RowHeightRuleEnum,
  TextBoxWrap, VAlign, CommentAnchor, ContentControlAnchor,
  defaultFootnoteSetup, defaultEndnoteSetup,
} from './types'
import { emptyContentControl } from './schema'
import type { EditorDocument } from './fromCdm'

export function docToCdm(edited: EditorDocument): CanonicalDocument {
  const sections = (edited.pmDoc.content ?? []).map(pmToSection)
  return {
    schemaVersion: edited.schemaVersion,
    properties: edited.properties,
    sections,
    // The server always re-discovers this itself on save (CanonicalMonikerScanner.cs) rather than
    // trusting whatever the client sends — see DocumentConversionService.SaveCanonicalTemplateAsync.
    monikers: [],
    footnotes: edited.footnotes,
    endnotes: edited.endnotes,
    comments: edited.comments,
    localization: edited.localization ?? null,
  }
}

function pmToSection(node: JSONContent): DocSection {
  const a = node.attrs ?? {}
  return {
    page: a.page as PageSetup,
    header: (a.header as HeaderFooter | null) ?? null,
    footer: (a.footer as HeaderFooter | null) ?? null,
    firstPageHeader: (a.firstPageHeader as HeaderFooter | null) ?? null,
    firstPageFooter: (a.firstPageFooter as HeaderFooter | null) ?? null,
    evenPageHeader: (a.evenPageHeader as HeaderFooter | null) ?? null,
    evenPageFooter: (a.evenPageFooter as HeaderFooter | null) ?? null,
    body: pmToBlocks(node.content ?? []),
    footnoteSetup: (a.footnoteSetup as NoteSetup) ?? defaultFootnoteSetup(),
    endnoteSetup: (a.endnoteSetup as NoteSetup) ?? defaultEndnoteSetup(),
  }
}

/** Exported for footnote/endnote body conversion (identical shape to section body) and testing. */
export function pmToBlocks(nodes: JSONContent[]): DocBlock[] {
  return nodes.map(pmToBlock)
}

function pmToBlock(node: JSONContent): DocBlock {
  const a = node.attrs ?? {}
  switch (node.type) {
    case 'paragraph':
      return { $type: 'paragraph', format: a.format as ParagraphFormat, inlines: pmToInlines(node.content ?? []) }

    case 'heading':
      return { $type: 'heading', level: (a.level as number) ?? 1, format: a.format as ParagraphFormat, inlines: pmToInlines(node.content ?? []) }

    case 'list':
      return {
        $type: 'list',
        ordered: (a.ordered as boolean) ?? false,
        startAt: (a.startAt as number | null) ?? null,
        items: (node.content ?? []).map(item => ({
          level: (item.attrs?.level as number) ?? 0,
          numberFormat: (item.attrs?.numberFormat as ListNumberFormat) ?? ListNumberFormatEnum.Decimal,
          content: pmToBlocks(item.content ?? []),
        })),
      }

    case 'table':
      return pmToTable(node)

    case 'image':
      return {
        $type: 'image',
        blobRef: (a.blobRef as string) ?? '',
        widthPt: (a.widthPt as number) ?? 0,
        heightPt: (a.heightPt as number) ?? 0,
        altText: (a.altText as string | null) ?? null,
        sourceMoniker: (a.sourceMoniker as string | null) ?? null,
        resolvedSource: (a.resolvedSource as string | null) ?? null,
        wrap: (a.wrap as TextBoxWrapType) ?? TextBoxWrap.Inline,
        anchor: (a.anchor as FloatingAnchor | null) ?? null,
        svgBlobRef: (a.svgBlobRef as string | null) ?? null,
      }

    case 'pageBreak':
      return { $type: 'pageBreak' }

    case 'horizontalRule':
      return { $type: 'horizontalRule' }

    case 'textBox':
      return {
        $type: 'textBox',
        content: pmToBlocks(node.content ?? []),
        widthPt: (a.widthPt as number) ?? 0,
        heightPt: (a.heightPt as number) ?? 0,
        fillHex: (a.fillHex as string | null) ?? null,
        border: (a.border as BorderSide | null) ?? null,
        wrap: (a.wrap as TextBoxWrapType) ?? TextBoxWrap.Inline,
        anchor: (a.anchor as FloatingAnchor | null) ?? null,
        vAlign: (a.vAlign as VAlignType) ?? VAlign.Top,
        paddingLeftPt: (a.paddingLeftPt as number | null) ?? null,
        paddingRightPt: (a.paddingRightPt as number | null) ?? null,
        paddingTopPt: (a.paddingTopPt as number | null) ?? null,
        paddingBottomPt: (a.paddingBottomPt as number | null) ?? null,
        name: (a.name as string | null) ?? null,
      }

    case 'toc':
      return { $type: 'toc', content: (a.content as DocBlock[]) ?? [] }

    case 'embeddedObject':
      return {
        $type: 'embeddedObject',
        kind: (a.kind as EmbeddedObjectKind) ?? 0,
        xml: (a.xml as string) ?? '',
        widthPt: (a.widthPt as number) ?? 0,
        heightPt: (a.heightPt as number) ?? 0,
        name: (a.name as string | null) ?? null,
        previewBlobRef: (a.previewBlobRef as string | null) ?? null,
        parts: (a.parts as EmbeddedPart[]) ?? [],
      }

    case 'contentControl':
      return {
        $type: 'contentControl',
        control: (a.control as ContentControl) ?? emptyContentControl(),
        content: pmToBlocks(node.content ?? []),
      }

    case 'loop':
      return {
        $type: 'loop',
        rawName: (a.rawName as string) ?? '',
        normalizedKey: (a.normalizedKey as string) ?? '',
        itemAlias: (a.itemAlias as string) ?? 'item',
        body: pmToBlocks(node.content ?? []),
        source: a.source as SourceLocation,
      }

    case 'conditional': {
      const branches = node.content ?? []
      const bodyBranch = branches.find(b => b.attrs?.kind === 'body')
      const elseBranch = branches.find(b => b.attrs?.kind === 'else')
      return {
        $type: 'conditional',
        rawName: (a.rawName as string) ?? '',
        normalizedKey: (a.normalizedKey as string) ?? '',
        negate: (a.negate as boolean) ?? false,
        body: pmToBlocks(bodyBranch?.content ?? []),
        elseBody: elseBranch ? pmToBlocks(elseBranch.content ?? []) : null,
        source: a.source as SourceLocation,
      }
    }

    case 'unknownBlock':
      return a.json as DocBlock

    default:
      throw new Error(`toCdm: unhandled ProseMirror block node type "${node.type}"`)
  }
}

/**
 * Reconstructs CDM's IsMergedContinuation cells from Tiptap's native colspan/rowspan-only table
 * representation, mirroring DocxCanonicalParser.ParseTable's own rowspanCounters algorithm exactly
 * (same "carry a merge forward by column index across rows" logic, ported from C# to TS) — a
 * rowspan-merged cell occupies its own real slot on its first row and a synthesized, empty,
 * IsMergedContinuation cell at the same column index on each subsequent row it spans.
 */
function pmToTable(node: JSONContent): DocBlock {
  const a = node.attrs ?? {}
  const pmRows = node.content ?? []
  const rows: TableRow[] = []
  const rowspanCounters = new Map<number, { remaining: number; cell: TableCell }>()

  for (const pmRow of pmRows) {
    const rawCells = pmRow.content ?? []
    const isHeader = rawCells.length > 0 && rawCells.every(c => c.type === 'tableHeader')
    const cells: TableCell[] = []
    let colIdx = 0
    let srcIdx = 0

    while (srcIdx < rawCells.length || rowspanCounters.has(colIdx)) {
      const spanned = rowspanCounters.get(colIdx)
      if (spanned) {
        // control: null, mirroring the parser — a synthesized continuation cell is not a cell the
        // source wrapped in a w:sdtCell, so it must not claim the origin cell's control.
        cells.push({ columnLoop: null, colSpan: spanned.cell.colSpan, rowSpan: 1, isMergedContinuation: true, format: spanned.cell.format, content: [], control: null, propertiesChange: null })
        spanned.remaining -= 1
        if (spanned.remaining <= 0) rowspanCounters.delete(colIdx)
        colIdx += spanned.cell.colSpan
        continue
      }

      if (srcIdx >= rawCells.length) break
      const pmCell = rawCells[srcIdx++]
      const cAttrs = pmCell.attrs ?? {}
      const colSpan = (cAttrs.colspan as number) ?? 1
      const rowSpan = (cAttrs.rowspan as number) ?? 1
      const cell: TableCell = {
        colSpan,
        rowSpan,
        isMergedContinuation: false,
        format: cAttrs.format as CellFormat,
        content: pmToBlocks(pmCell.content ?? []),
        control: (cAttrs.control as ContentControl | null) ?? null,
        propertiesChange: (cAttrs.propertiesChange as PropertiesChange | null) ?? null,
        columnLoop: (cAttrs.columnLoop as TableColumnLoop | null) ?? null,
      }
      if (rowSpan > 1) rowspanCounters.set(colIdx, { remaining: rowSpan - 1, cell })
      cells.push(cell)
      colIdx += colSpan
    }

    rows.push({
      cells,
      isHeader,
      heightPt: (pmRow.attrs?.heightPt as number | null) ?? null,
      heightRule: (pmRow.attrs?.heightRule as RowHeightRule) ?? RowHeightRuleEnum.Auto,
      cantSplit: (pmRow.attrs?.cantSplit as boolean) ?? false,
      control: (pmRow.attrs?.control as ContentControl | null) ?? null,
      revision: (pmRow.attrs?.revision as Revision | null) ?? null,
    })
  }

  return {
    $type: 'table',
    columnWidthsPt: (a.columnWidthsPt as number[] | null) ?? null,
    borders: a.borders as TableBorders,
    rows,
    alignment: (a.alignment as TableAlignment) ?? TableAlignmentEnum.Left,
    // Preserved as null when absent, never coerced to Auto: null is what tells the DOCX renderer
    // this table predates schema v17 and must keep its historical full width.
    width: (a.width as TableWidth | null) ?? null,
    indentPt: (a.indentPt as number | null) ?? null,
    layout: (a.layout as TableLayout) ?? TableLayoutEnum.Auto,
    cellSpacingPt: (a.cellSpacingPt as number | null) ?? null,
    propertiesChange: (a.propertiesChange as PropertiesChange | null) ?? null,
  }
}

export function pmToInlines(nodes: JSONContent[]): DocInline[] {
  return nodes.map(pmToInline)
}

function pmToInline(node: JSONContent): DocInline {
  const a = node.attrs ?? {}
  switch (node.type) {
    case 'text': {
      const mark = (node.marks ?? []).find(m => m.type === 'runFormat')
      return { $type: 'run', text: node.text ?? '', format: (mark?.attrs?.format as RunFormat) ?? defaultRunFormat() }
    }

    case 'hardBreak':
      return { $type: 'break' }

    case 'tabStop':
      return { $type: 'tab' }

    case 'placeholder':
      return {
        $type: 'placeholder',
        rawName: (a.rawName as string) ?? '',
        normalizedKey: (a.normalizedKey as string) ?? '',
        formatters: (a.formatters as FormatterSpec[] | null) ?? null,
        format: (a.format as RunFormat) ?? defaultRunFormat(),
        source: a.source as SourceLocation,
      }

    case 'field':
      return {
        $type: 'field',
        fieldType: (a.fieldType as FieldType) ?? 0,
        staticText: (a.staticText as string) ?? '',
        format: (a.format as RunFormat) ?? defaultRunFormat(),
        instruction: (a.instruction as string | null) ?? null,
      }

    case 'hyperlinkRun':
      return {
        $type: 'hyperlink',
        text: (a.text as string) ?? '',
        url: (a.url as string | null) ?? null,
        anchorName: (a.anchorName as string | null) ?? null,
        format: (a.format as RunFormat) ?? defaultRunFormat(),
      }

    case 'footnoteRef':
      return { $type: 'footnote', footnoteId: (a.footnoteId as number) ?? 0 }

    case 'endnoteRef':
      return { $type: 'endnote', endnoteId: (a.endnoteId as number) ?? 0 }

    case 'bookmark':
      return { $type: 'bookmark', name: (a.name as string) ?? '' }

    case 'equation':
      return {
        $type: 'equation',
        xml: (a.xml as string) ?? '',
        text: (a.text as string) ?? '',
        isDisplay: (a.isDisplay as boolean) ?? false,
        format: (a.format as RunFormat) ?? defaultRunFormat(),
      }

    case 'deleted':
      return {
        $type: 'deleted',
        text: (a.text as string) ?? '',
        format: (a.format as RunFormat) ?? defaultRunFormat(),
        revision: (a.revision as Revision) ?? { kind: 0, author: '', date: null },
      }

    case 'contentControlMarker':
      return {
        $type: 'contentControl',
        controlId: (a.controlId as number) ?? 0,
        anchor: (a.anchor as ContentControlAnchorType) ?? ContentControlAnchor.Start,
        control: (a.control as ContentControl | null) ?? null,
      }

    case 'comment':
      return {
        $type: 'comment',
        commentId: (a.commentId as number) ?? 0,
        anchor: (a.anchor as CommentAnchorType) ?? CommentAnchor.Reference,
      }

    case 'conditionalRun':
      return a.json as ConditionalRun
    case 'loopRun':
      return a.json as LoopRun

    case 'unknownInline':
      return a.json as DocInline

    default:
      throw new Error(`toCdm: unhandled ProseMirror inline node type "${node.type}"`)
  }
}
